import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, Tooltip } from "antd";
import {
  GlobalOutlined,
  InfoCircleOutlined,
  InstagramOutlined,
  ShopOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { SONIC_PROSPECT_PRICING, formatProspectUsdTr } from "util/prospectBilling";
import styles from "./style.module.scss";

const PRODUCTS = [
  {
    key: "channels",
    title: "Channels",
    body: "sonic_prospect.product_channels_body",
    detail: "sonic_prospect.product_channels_detail",
    surfaces: [
      { id: "instagram", title: "Instagram", body: "sonic_prospect.surface_instagram", icon: "instagram" },
      { id: "whatsapp", title: "WhatsApp", body: "sonic_prospect.surface_whatsapp", icon: "whatsapp" },
      { id: "website", title: "Website", body: "sonic_prospect.surface_website", icon: "web" },
    ],
  },
  {
    key: "ecommerce",
    title: "sonic_prospect.shopify_card_title",
    body: "sonic_prospect.shopify_card_body",
    detail: "sonic_prospect.shopify_card_body",
    surfaces: [],
  },
];

const STEPS = [
  { id: 1, label: "sonic_prospect.step_product" },
  { id: 2, label: "sonic_prospect.step_integration" },
  { id: 3, label: "sonic_prospect.step_done" },
];

const ICONS = {
  instagram: InstagramOutlined,
  whatsapp: WhatsAppOutlined,
  web: GlobalOutlined,
  shop: ShopOutlined,
};

const ProductHome = ({
  step,
  productKey,
  memberships,
  onAccept,
  onAdvance,
  onStart,
  onDone,
  busyKey,
}) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState(null);
  const accepted = new Set((memberships || []).map((row) => row.product_key));
  const { freeLifetimeQp, tier1Limit, tier1Price, tier2Price } = SONIC_PROSPECT_PRICING;
  const tier1Label = formatProspectUsdTr(tier1Price);
  const tier2Label = formatProspectUsdTr(tier2Price);
  const listingUrl = SONIC_PROSPECT_PRICING.shopifyAppStoreUrl;
  const product = PRODUCTS.find((item) => item.key === productKey) || PRODUCTS[0];
  const tierTip = (
    <div>
      <div>{t("sonic_prospect.price_tier_free", { count: freeLifetimeQp })}</div>
      <div>{t("sonic_prospect.price_tier_1", { price: tier1Label, count: tier1Limit })}</div>
      <div>{t("sonic_prospect.price_tier_2", { price: tier2Label })}</div>
    </div>
  );

  const choose = async (item) => {
    if (!accepted.has(item.key)) {
      setPending(item);
      return;
    }
    onAdvance(item.key);
  };

  return (
    <div className={styles.productHome}>
      <ol className={styles.stepRail} aria-label={t("sonic_prospect.step_rail_label")}>
        {STEPS.map((item) => (
          <li
            key={item.id}
            className={item.id === step ? styles.stepOn : styles.stepIdle}
            aria-current={item.id === step ? "step" : undefined}
          >
            <span className={styles.stepDot} aria-hidden="true" />
            {t(item.label)}
          </li>
        ))}
      </ol>

      {step === 3 ? (
        <div className={styles.onboardDone}>
          <h2>{t("sonic_prospect.onboard_done_title")}</h2>
          <p>{t("sonic_prospect.onboard_done_body")}</p>
          <Button type="primary" onClick={onDone}>
            {t("sonic_prospect.onboard_done_cta")}
          </Button>
        </div>
      ) : null}

      {step === 1 ? (
        <>
          <h1 className={styles.onboardHeadline}>{t("sonic_prospect.home_title")}</h1>
          <p className={styles.productLede}>{t("sonic_prospect.home_lede")}</p>
          <div className={styles.productGrid}>
            {PRODUCTS.map((item) => (
              <article key={item.key} className={styles.productCard}>
                {item.key === "channels" ? (
                  <span className={styles.recommendBadge}>
                    {t("sonic_prospect.recommended")}
                  </span>
                ) : null}
                <h2>{item.key === "channels" ? item.title : t(item.title)}</h2>
                <p>{t(item.body)}</p>
                {item.key === "ecommerce" ? (
                  <p className={styles.priceLine}>
                    {t("sonic_prospect.shopify_price_line", { count: freeLifetimeQp })}
                    <Tooltip title={tierTip} color="var(--ink)" overlayClassName={styles.priceTip}>
                      <button type="button" className={styles.priceInfo} aria-label={t("sonic_prospect.price_info_label")}>
                        <InfoCircleOutlined />
                      </button>
                    </Tooltip>
                  </p>
                ) : (
                  <div className={styles.priceLine}>
                    <p>{t("sonic_prospect.price_free_line", { count: freeLifetimeQp })}</p>
                    <p>
                      {t("sonic_prospect.price_paid_line", {
                        price: tier1Label,
                        count: tier1Limit,
                        after: tier2Label,
                      })}
                    </p>
                  </div>
                )}
                {item.key === "channels" ? (
                  <p className={styles.detailCopy}>{t(item.detail)}</p>
                ) : null}
                {item.key === "ecommerce" ? (
                  <Button
                    type="primary"
                    className={styles.membershipBtn}
                    href={listingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("sonic_prospect.shopify_install_cta")}
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    className={styles.membershipBtn}
                    loading={busyKey === item.key}
                    onClick={() => choose(item)}
                  >
                    {t("sonic_prospect.start_membership")}
                  </Button>
                )}
              </article>
            ))}
          </div>
        </>
      ) : null}

      {step === 2 ? (
        <>
          <h1 className={styles.onboardHeadline}>{t(product.title, { defaultValue: product.title })}</h1>
          <p className={styles.productLede}>{t("sonic_prospect.step_pick_integration")}</p>
          <ul className={styles.integrationList}>
            {product.surfaces.map((surface) => {
              const Icon = ICONS[surface.icon] || GlobalOutlined;
              return (
                <li key={surface.id}>
                  <span className={styles.integrationIcon} aria-hidden="true">
                    <Icon />
                  </span>
                  <div className={styles.integrationCopy}>
                    <strong>{surface.title}</strong>
                    <span>{t(surface.body)}</span>
                  </div>
                  <Button
                    type="primary"
                    loading={busyKey === surface.id}
                    onClick={() => onStart(product.key, surface.id)}
                  >
                    {t("sonic_prospect.start_integration")}
                  </Button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      <Modal
        open={!!pending}
        title={t("sonic_prospect.confirm_title", { product: pending?.title || "" })}
        onCancel={() => setPending(null)}
        footer={[
          <Button key="cancel" onClick={() => setPending(null)}>
            {t("sonic_prospect.confirm_cancel")}
          </Button>,
          <Button
            key="accept"
            type="primary"
            loading={busyKey === pending?.key}
            onClick={async () => {
              if (!pending) return;
              await onAccept(pending.key);
              const key = pending.key;
              setPending(null);
              onAdvance(key);
            }}
          >
            {t("sonic_prospect.confirm_accept")}
          </Button>,
        ]}
      >
        <p>
          {t("sonic_prospect.confirm_body", {
            free: freeLifetimeQp,
            launch: tier1Label,
            standard: tier2Label,
            count: tier1Limit,
          })}
        </p>
      </Modal>
    </div>
  );
};

export default ProductHome;
